import type { FC } from "react";

import dompurify from "isomorphic-dompurify";

import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import type { FreeGift } from "@jti/magento";
import { useSelector } from "@jti/store/reduxHooks";
import { selectFreeGiftsErrors } from "@jti/store/selectors/selectFreeGiftsErrors";
import { I18n, useTranslations } from "@jti/ui";

import ProductFreeGifts from "@checkout/components/FreeGifts/ProductFreeGifts/ProductFreeGifts";
import type { SelectedFreeGifts, SetSelectedFreeGiftQuantity } from "@checkout/hooks/useFreeGifts";

import styles from "./GiftBlock.module.scss";

const { sanitize } = dompurify;

type GiftBlockProps = {
  parentItemId: string;
  parentItemSku: string;
  limit: number;
  freeGifts: FreeGift[];
  selectedFreeGifts: SelectedFreeGifts;
  setSelectedFreeGiftQuantity: SetSelectedFreeGiftQuantity;
  hasMultipleProductsWithGifts: boolean;
};

const renderTitle = (selectedQty: number, limit: number) => {
  if (selectedQty === 0) {
    return (
      <I18n t="cart.free_gifts.alertStart">
        <b>{selectedQty}</b>
        <b>{limit}</b>
      </I18n>
    );
  }

  if (selectedQty < limit) {
    return (
      <I18n t="cart.free_gifts.alertMore">
        <b>{selectedQty}</b>
        <b>{limit}</b>
        <b>{limit - selectedQty}</b>
      </I18n>
    );
  }

  return (
    <I18n t="cart.free_gifts.alertEnd">
      <b>{limit}</b>
    </I18n>
  );
};

export const GiftBlock: FC<GiftBlockProps> = ({
  parentItemId,
  parentItemSku,
  limit,
  freeGifts,
  selectedFreeGifts,
  setSelectedFreeGiftQuantity,
  hasMultipleProductsWithGifts,
}) => {
  const { __ } = useTranslations();

  const selectedFreeGiftsForProduct = selectedFreeGifts?.[parentItemId];
  const selectedQty = selectedFreeGiftsForProduct
    ? Object.values(selectedFreeGiftsForProduct).reduce((acc, qty) => acc + qty, 0)
    : 0;

  const hasReachedLimit = selectedQty >= limit;

  const freeGiftsErrors = useSelector(selectFreeGiftsErrors);
  const hasErrors = freeGiftsErrors.some(
    (freeGiftError) => freeGiftError.parentSku === parentItemSku && freeGiftError.parentId === parentItemId
  );
  const errorMessages =
    freeGiftsErrors
      ?.filter((freeGiftError) => freeGiftError.parentSku === parentItemSku && freeGiftError.parentId === parentItemId)
      .map((freeGiftError) => (
        <span
          key={freeGiftError.errorCode + freeGiftError.parentId + freeGiftError.parentSku}
          dangerouslySetInnerHTML={{
            __html: sanitize(__(`cart.free_gifts.errors.${freeGiftError.errorCode}`, [selectedQty, limit])),
          }}
        ></span>
      )) || [];

  return (
    <div className={styles.giftBlockWrapper}>
      {!hasErrors && (
        <AlertBanner tone={hasReachedLimit ? "success" : "info"} richTitle={renderTitle(selectedQty, limit)} />
      )}

      {hasErrors && <AlertBanner tone="warning" richTitle={errorMessages} />}

      <ProductFreeGifts
        parentItemSku={parentItemSku}
        parentItemId={parentItemId}
        hasReachedBasketLimit={hasReachedLimit}
        limit={limit}
        freeGifts={freeGifts}
        selectedFreeGifts={selectedFreeGifts}
        setSelectedFreeGiftQuantity={setSelectedFreeGiftQuantity}
        hasMultipleProductsWithGifts={hasMultipleProductsWithGifts}
      />
    </div>
  );
};
